const MODAL_SELECTOR = '#df_media_modal';
const CONTENT_SELECTOR = '.df-media-modal-content';
const ROTATE_BUTTONS_SELECTOR = '.df-media-modal-rotate-buttons';
const ROTATE_SPINNER_SELECTOR = '.df-media-modal-rotate-spinner';
let dfMediaModalEventsBound = false;
let currentDfMediaModalTrigger = null;

const mediaItemsFromTrigger = (trigger) => {
    try {
        return JSON.parse(trigger.dataset.dfMediaItems || '[]');
    } catch (error) {
        return [];
    }
};

// Resolve the .open-trim-video-modal scissors button associated with a given media trigger.
// Trimming is only available for video-upload elements (and videos uploaded through the same
// partial): those rows render a scissors button, while file-upload rows have it removed. Look in
// the trigger's own row first (fresh upload/record rows keep scissors + media button as siblings),
// then fall back to the surrounding .parent-block (server-rendered validate rows).
const scissorsForTrigger = (trigger) => {
    if (!trigger) return null;
    const row = trigger.closest('.template-upload, .template-download');
    return (row && row.querySelector('.open-trim-video-modal'))
        || trigger.closest('.parent-block')?.querySelector('.open-trim-video-modal')
        || null;
};

const imageElement = (item) => {
    const image = document.createElement('img');
    image.src = item.src;
    image.className = 'img-fluid h-95 max-w-100';
    if (item.alt) image.alt = item.alt;
    return image;
};

const videoElement = (item) => {
    const video = document.createElement('video');
    video.className = 'h-92 max-w-100';
    video.controls = true;
    video.playsInline = true;
    if (item.poster) video.poster = item.poster;

    const sources = item.sources?.length ? item.sources : [{ src: item.src, type: item.mimeType }];
    sources.forEach((source) => {
        if (!source.src) return;

        const element = document.createElement('source');
        element.src = source.src;
        if (source.type) element.type = source.type;
        video.appendChild(element);
    });

    return video;
};

const mediaElement = (item) => {
    const el = item.type === 'video' ? videoElement(item) : imageElement(item);
    const name = item.alt && item.alt !== 'video' ? item.alt : null;
    const isVideo = item.type === 'video';

    if (!name && !isVideo) return el;

    const wrapper = document.createElement('div');
    wrapper.className = 'df-media-modal-item-wrapper';
    wrapper.appendChild(el);

    const footer = document.createElement('div');
    footer.className = `df-media-modal-item-footer d-flex align-items-center ${isVideo && item.canTrim ? 'justify-content-between w-100 px-4' : 'justify-content-center' } gap-2 my-2`;

    if (name) {
        const caption = document.createElement('p');
        caption.className = 'df-media-modal-item-name text-white text-center small mb-0 txt2';
        caption.title = name;
        caption.textContent = name;
        footer.appendChild(caption);
    }

    // Only offer trimming for videos that came from a trim-capable element (video_upload and
    // videos uploaded through the same partial). item.canTrim is set in groupedMediaItems based
    // on whether the source trigger has an .open-trim-video-modal scissors button.
    if (isVideo && item.canTrim) {
        const trimBtn = document.createElement('button');
        trimBtn.type = 'button';
        trimBtn.className = 'btn btn-primary df-media-modal-trim-btn my-0 ms-2';
        trimBtn.title = 'Trim video';
        trimBtn.innerHTML = '<i class="fa-solid fa-scissors txt3 me-1"></i> Trim Video';
        footer.appendChild(trimBtn);
    }

    wrapper.appendChild(footer);
    return wrapper;
};

const sliderElement = (items, activeIndex = 0) => {
    const carouselId = 'df_media_modal_carousel';
    const carousel = document.createElement('div');
    carousel.id = carouselId;
    carousel.className = 'carousel slide';
    carousel.dataset.bsRide = 'false';

    const inner = document.createElement('div');
    inner.className = 'carousel-inner';

    items.forEach((item, index) => {
        const slide = document.createElement('div');
        slide.className = `carousel-item${index === activeIndex ? ' active' : ''}`;
        slide.appendChild(mediaElement(item));
        inner.appendChild(slide);
    });

    carousel.appendChild(inner);

    if (items.length > 1) {
        carousel.insertAdjacentHTML('beforeend',
            `<button class="btn apl-glas-effect carousel-control-prev rounded-circle" type="button" data-bs-target="#${carouselId}" data-bs-slide="prev">
                <i class="cww-icons ico-chevron-left fs-title-0" aria-hidden="true"></i>
                <span class="visually-hidden">Previous</span>
            </button>
            <button class="btn apl-glas-effect carousel-control-next rounded-circle" type="button" data-bs-target="#${carouselId}" data-bs-slide="next">
                <i class="cww-icons ico-chevron-right fs-title-0" aria-hidden="true"></i>
                <span class="visually-hidden">Next</span>
            </button>`
        );
    }

    return carousel;
};

const TRIGGER_SELECTOR = '[data-bs-target="#df_media_modal"][data-df-media-items]';
const GROUP_SELECTOR = '.files, .fb-element-dynamic-units, .form-row';
const groupedMediaItems = (trigger) => {
    const group = trigger.closest(GROUP_SELECTOR);
    const triggers = group ? Array.from(group.querySelectorAll(TRIGGER_SELECTOR)) : [trigger];

    const items = [];
    let activeIndex = 0;
    triggers.forEach((groupTrigger) => {
        const triggerItems = mediaItemsFromTrigger(groupTrigger);
        if (!triggerItems.length) return;
        if (groupTrigger === trigger) activeIndex = items.length;
        const canTrim = !!scissorsForTrigger(groupTrigger);
        triggerItems.forEach((item) => { item.canTrim = canTrim; });
        items.push(...triggerItems);
    });

    if (!items.length) {
        const fallback = mediaItemsFromTrigger(trigger);
        const canTrim = !!scissorsForTrigger(trigger);
        fallback.forEach((item) => { item.canTrim = canTrim; });
        return { items: fallback, activeIndex: 0 };
    }

    return { items, activeIndex };
};

const renderDfMediaModal = (modal, trigger) => {
    const contentContainer = modal.querySelector(CONTENT_SELECTOR);
    if (!contentContainer) return;

    const { items, activeIndex } = trigger ? groupedMediaItems(trigger) : { items: [], activeIndex: 0 };

    contentContainer.replaceChildren();
    if (items.length === 0) return;

    contentContainer.appendChild(items.length === 1 ? mediaElement(items[0]) : sliderElement(items, activeIndex));
};

const activeModalMediaTrigger = (modal) => {
    if (!modal || !currentDfMediaModalTrigger) return null;

    const carousel = modal.querySelector('#df_media_modal_carousel');
    if (!carousel) return currentDfMediaModalTrigger;

    const activeItem = carousel.querySelector('.carousel-item.active');
    const activeIndex = Array.from(carousel.querySelectorAll('.carousel-item')).indexOf(activeItem);
    const group = currentDfMediaModalTrigger.closest(GROUP_SELECTOR);
    const triggers = group ? Array.from(group.querySelectorAll(TRIGGER_SELECTOR)) : [currentDfMediaModalTrigger];

    return triggers[activeIndex] || currentDfMediaModalTrigger;
};

const activeScissorsTrigger = (modal) => {
    const trigger = activeModalMediaTrigger(modal);
    return scissorsForTrigger(trigger);
};

const mediaTriggerCanRotate = (trigger) => (
    !!trigger
    && !!trigger.closest('.parent-block')?.querySelector('.rotate-buttons')
    && ['image', 'video'].includes(trigger.dataset.dfMediaType)
);

const updateModalRotateControls = (modal) => {
    const rotateButtons = modal.querySelector(ROTATE_BUTTONS_SELECTOR);
    const rotateSpinner = modal.querySelector(ROTATE_SPINNER_SELECTOR);
    if (!rotateButtons) return;

    const isRotating = modal.dataset.dfMediaModalRotating === 'true';
    rotateButtons.classList.toggle('d-none', isRotating || !mediaTriggerCanRotate(activeModalMediaTrigger(modal)));
    rotateButtons.querySelectorAll('button').forEach((button) => {
        button.disabled = isRotating;
    });
    if (rotateSpinner) rotateSpinner.classList.toggle('d-none', !isRotating);
};

const setDfMediaModalRotating = (modal, isRotating) => {
    if (!modal) return;

    modal.dataset.dfMediaModalRotating = isRotating ? 'true' : 'false';
    updateModalRotateControls(modal);
};

const refreshDfMediaModalFromTrigger = (trigger) => {
    const modal = document.querySelector(MODAL_SELECTOR);
    if (!modal || !modal.classList.contains('show')) return;

    currentDfMediaModalTrigger = trigger || currentDfMediaModalTrigger;
    renderDfMediaModal(modal, currentDfMediaModalTrigger);
    setDfMediaModalRotating(modal, false);
    updateModalRotateControls(modal);
};

const clearDfMediaModal = (modal) => {
    const contentContainer = modal.querySelector(CONTENT_SELECTOR);
    if (!contentContainer) return;

    contentContainer.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.currentTime = 0;
    });
    contentContainer.replaceChildren();
    currentDfMediaModalTrigger = null;
};

const bindDfMediaModalEvents = () => {
    if (dfMediaModalEventsBound || window.dfMediaModalEventsBound) return;
    dfMediaModalEventsBound = true;
    window.dfMediaModalEventsBound = true;

    document.addEventListener('show.bs.modal', (event) => {
        if (!event.target.matches(MODAL_SELECTOR)) return;
        setDfMediaModalRotating(event.target, false);
        currentDfMediaModalTrigger = event.relatedTarget;
        renderDfMediaModal(event.target, currentDfMediaModalTrigger);
        updateModalRotateControls(event.target);
    });

    document.addEventListener('hidden.bs.modal', (event) => {
        if (!event.target.matches(MODAL_SELECTOR)) return;
        setDfMediaModalRotating(event.target, false);
        clearDfMediaModal(event.target);
    });

    document.addEventListener('slid.bs.carousel', (event) => {
        const modal = event.target.closest(MODAL_SELECTOR);
        if (!modal) return;

        updateModalRotateControls(modal);
    });

    document.addEventListener('click', (event) => {
        const button = event.target.closest('.df-media-modal-trim-btn');
        if (!button) return;

        const modal = button.closest(MODAL_SELECTOR);
        if (!modal) return;

        const scissors = activeScissorsTrigger(modal);
        if (!scissors) return;

        event.preventDefault();
        const bsModal = window.bootstrap?.Modal?.getInstance(modal);
        if (bsModal) {
            modal.addEventListener('hidden.bs.modal', () => { scissors.click(); }, { once: true });
            bsModal.hide();
        } else {
            scissors.click();
        }
    });

    document.addEventListener('click', (event) => {
        const button = event.target.closest('.df-media-modal-rotate-button');
        if (!button) return;

        const modal = button.closest(MODAL_SELECTOR);
        if (modal?.dataset.dfMediaModalRotating === 'true') return;

        const trigger = activeModalMediaTrigger(modal);
        if (!mediaTriggerCanRotate(trigger)) return;

        event.preventDefault();
        currentDfMediaModalTrigger = trigger;
        setDfMediaModalRotating(modal, true);

        const degree = Number(button.dataset.degree);
        if (trigger.dataset.dfMediaType === 'image' && typeof window.rotateImage === 'function') {
            window.rotateImage(trigger, degree);
        } else if (trigger.dataset.dfMediaType === 'video' && typeof window.rotateVideo === 'function') {
            window.rotateVideo(trigger, degree);
        } else {
            setDfMediaModalRotating(modal, false);
        }
    });
};

const initializeDfMediaModal = () => {
    bindDfMediaModalEvents();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDfMediaModal);
} else {
    initializeDfMediaModal();
}

document.addEventListener('turbolinks:load', initializeDfMediaModal);

window.initializeDfMediaModal = initializeDfMediaModal;
window.refreshDfMediaModalFromTrigger = refreshDfMediaModalFromTrigger;
window.setDfMediaModalRotating = (isRotating) => setDfMediaModalRotating(document.querySelector(MODAL_SELECTOR), isRotating);
const MODAL_SELECTOR = '#df_media_modal';
const CONTENT_SELECTOR = '.df-media-modal-content';
const ROTATE_BUTTONS_SELECTOR = '.df-media-modal-rotate-buttons';
const ROTATE_SPINNER_SELECTOR = '.df-media-modal-rotate-spinner';
let dfMediaModalEventsBound = false;
let currentDfMediaModalTrigger = null;

const mediaItemsFromTrigger = (trigger) => {
    try {
        return JSON.parse(trigger.dataset.dfMediaItems || '[]');
    } catch (error) {
        return [];
    }
};

// Resolve the .open-trim-video-modal scissors button associated with a given media trigger.
// Trimming is only available for video-upload elements (and videos uploaded through the same
// partial): those rows render a scissors button, while file-upload rows have it removed. Look in
// the trigger's own row first (fresh upload/record rows keep scissors + media button as siblings),
// then fall back to the surrounding .parent-block (server-rendered validate rows).
const scissorsForTrigger = (trigger) => {
    if (!trigger) return null;
    const row = trigger.closest('.template-upload, .template-download');
    return (row && row.querySelector('.open-trim-video-modal'))
        || trigger.closest('.parent-block')?.querySelector('.open-trim-video-modal')
        || null;
};

const imageElement = (item) => {
    const image = document.createElement('img');
    image.src = item.src;
    image.className = 'img-fluid h-95 max-w-100';
    if (item.alt) image.alt = item.alt;
    return image;
};

const videoElement = (item) => {
    const video = document.createElement('video');
    video.className = 'h-92 max-w-100';
    video.controls = true;
    video.playsInline = true;
    if (item.poster) video.poster = item.poster;

    const sources = item.sources?.length ? item.sources : [{ src: item.src, type: item.mimeType }];
    sources.forEach((source) => {
        if (!source.src) return;

        const element = document.createElement('source');
        element.src = source.src;
        if (source.type) element.type = source.type;
        video.appendChild(element);
    });

    return video;
};

const mediaElement = (item) => {
    const el = item.type === 'video' ? videoElement(item) : imageElement(item);
    const name = item.alt && item.alt !== 'video' ? item.alt : null;
    const isVideo = item.type === 'video';

    if (!name && !isVideo) return el;

    const wrapper = document.createElement('div');
    wrapper.className = 'df-media-modal-item-wrapper';
    wrapper.appendChild(el);

    const footer = document.createElement('div');
    footer.className = `df-media-modal-item-footer d-flex align-items-center ${isVideo && item.canTrim ? 'justify-content-between w-100 px-4' : 'justify-content-center' } gap-2 my-2`;

    if (name) {
        const caption = document.createElement('p');
        caption.className = 'df-media-modal-item-name text-white text-center small mb-0 txt2';
        caption.title = name;
        caption.textContent = name;
        footer.appendChild(caption);
    }

    // Only offer trimming for videos that came from a trim-capable element (video_upload and
    // videos uploaded through the same partial). item.canTrim is set in groupedMediaItems based
    // on whether the source trigger has an .open-trim-video-modal scissors button.
    if (isVideo && item.canTrim) {
        const trimBtn = document.createElement('button');
        trimBtn.type = 'button';
        trimBtn.className = 'btn btn-primary df-media-modal-trim-btn my-0 ms-2';
        trimBtn.title = 'Trim video';
        trimBtn.innerHTML = '<i class="fa-solid fa-scissors txt3 me-1"></i> Trim Video';
        footer.appendChild(trimBtn);
    }

    wrapper.appendChild(footer);
    return wrapper;
};

const sliderElement = (items, activeIndex = 0) => {
    const carouselId = 'df_media_modal_carousel';
    const carousel = document.createElement('div');
    carousel.id = carouselId;
    carousel.className = 'carousel slide';
    carousel.dataset.bsRide = 'false';

    const inner = document.createElement('div');
    inner.className = 'carousel-inner';

    items.forEach((item, index) => {
        const slide = document.createElement('div');
        slide.className = `carousel-item${index === activeIndex ? ' active' : ''}`;
        slide.appendChild(mediaElement(item));
        inner.appendChild(slide);
    });

    carousel.appendChild(inner);

    if (items.length > 1) {
        carousel.insertAdjacentHTML('beforeend',
            `<button class="btn apl-glas-effect carousel-control-prev rounded-circle" type="button" data-bs-target="#${carouselId}" data-bs-slide="prev">
                <i class="cww-icons ico-chevron-left fs-title-0" aria-hidden="true"></i>
                <span class="visually-hidden">Previous</span>
            </button>
            <button class="btn apl-glas-effect carousel-control-next rounded-circle" type="button" data-bs-target="#${carouselId}" data-bs-slide="next">
                <i class="cww-icons ico-chevron-right fs-title-0" aria-hidden="true"></i>
                <span class="visually-hidden">Next</span>
            </button>`
        );
    }

    return carousel;
};

const TRIGGER_SELECTOR = '[data-bs-target="#df_media_modal"][data-df-media-items]';
const GROUP_SELECTOR = '.files, .fb-element-dynamic-units, .form-row';
const groupedMediaItems = (trigger) => {
    const group = trigger.closest(GROUP_SELECTOR);
    const triggers = group ? Array.from(group.querySelectorAll(TRIGGER_SELECTOR)) : [trigger];

    const items = [];
    let activeIndex = 0;
    triggers.forEach((groupTrigger) => {
        const triggerItems = mediaItemsFromTrigger(groupTrigger);
        if (!triggerItems.length) return;
        if (groupTrigger === trigger) activeIndex = items.length;
        const canTrim = !!scissorsForTrigger(groupTrigger);
        triggerItems.forEach((item) => { item.canTrim = canTrim; });
        items.push(...triggerItems);
    });

    if (!items.length) {
        const fallback = mediaItemsFromTrigger(trigger);
        const canTrim = !!scissorsForTrigger(trigger);
        fallback.forEach((item) => { item.canTrim = canTrim; });
        return { items: fallback, activeIndex: 0 };
    }

    return { items, activeIndex };
};

const renderDfMediaModal = (modal, trigger) => {
    const contentContainer = modal.querySelector(CONTENT_SELECTOR);
    if (!contentContainer) return;

    const { items, activeIndex } = trigger ? groupedMediaItems(trigger) : { items: [], activeIndex: 0 };

    contentContainer.replaceChildren();
    if (items.length === 0) return;

    contentContainer.appendChild(items.length === 1 ? mediaElement(items[0]) : sliderElement(items, activeIndex));
};

const activeModalMediaTrigger = (modal) => {
    if (!modal || !currentDfMediaModalTrigger) return null;

    const carousel = modal.querySelector('#df_media_modal_carousel');
    if (!carousel) return currentDfMediaModalTrigger;

    const activeItem = carousel.querySelector('.carousel-item.active');
    const activeIndex = Array.from(carousel.querySelectorAll('.carousel-item')).indexOf(activeItem);
    const group = currentDfMediaModalTrigger.closest(GROUP_SELECTOR);
    const triggers = group ? Array.from(group.querySelectorAll(TRIGGER_SELECTOR)) : [currentDfMediaModalTrigger];

    return triggers[activeIndex] || currentDfMediaModalTrigger;
};

const activeScissorsTrigger = (modal) => {
    const trigger = activeModalMediaTrigger(modal);
    return scissorsForTrigger(trigger);
};

const mediaTriggerCanRotate = (trigger) => (
    !!trigger
    && !!trigger.closest('.parent-block')?.querySelector('.rotate-buttons')
    && ['image', 'video'].includes(trigger.dataset.dfMediaType)
);

const updateModalRotateControls = (modal) => {
    const rotateButtons = modal.querySelector(ROTATE_BUTTONS_SELECTOR);
    const rotateSpinner = modal.querySelector(ROTATE_SPINNER_SELECTOR);
    if (!rotateButtons) return;

    const isRotating = modal.dataset.dfMediaModalRotating === 'true';
    rotateButtons.classList.toggle('d-none', isRotating || !mediaTriggerCanRotate(activeModalMediaTrigger(modal)));
    rotateButtons.querySelectorAll('button').forEach((button) => {
        button.disabled = isRotating;
    });
    if (rotateSpinner) rotateSpinner.classList.toggle('d-none', !isRotating);
};

const setDfMediaModalRotating = (modal, isRotating) => {
    if (!modal) return;

    modal.dataset.dfMediaModalRotating = isRotating ? 'true' : 'false';
    updateModalRotateControls(modal);
};

const refreshDfMediaModalFromTrigger = (trigger) => {
    const modal = document.querySelector(MODAL_SELECTOR);
    if (!modal || !modal.classList.contains('show')) return;

    currentDfMediaModalTrigger = trigger || currentDfMediaModalTrigger;
    renderDfMediaModal(modal, currentDfMediaModalTrigger);
    setDfMediaModalRotating(modal, false);
    updateModalRotateControls(modal);
};

const clearDfMediaModal = (modal) => {
    const contentContainer = modal.querySelector(CONTENT_SELECTOR);
    if (!contentContainer) return;

    contentContainer.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.currentTime = 0;
    });
    contentContainer.replaceChildren();
    currentDfMediaModalTrigger = null;
};

const bindDfMediaModalEvents = () => {
    if (dfMediaModalEventsBound || window.dfMediaModalEventsBound) return;
    dfMediaModalEventsBound = true;
    window.dfMediaModalEventsBound = true;

    document.addEventListener('show.bs.modal', (event) => {
        if (!event.target.matches(MODAL_SELECTOR)) return;
        setDfMediaModalRotating(event.target, false);
        currentDfMediaModalTrigger = event.relatedTarget;
        renderDfMediaModal(event.target, currentDfMediaModalTrigger);
        updateModalRotateControls(event.target);
    });

    document.addEventListener('hidden.bs.modal', (event) => {
        if (!event.target.matches(MODAL_SELECTOR)) return;
        setDfMediaModalRotating(event.target, false);
        clearDfMediaModal(event.target);
    });

    document.addEventListener('slid.bs.carousel', (event) => {
        const modal = event.target.closest(MODAL_SELECTOR);
        if (!modal) return;

        updateModalRotateControls(modal);
    });

    document.addEventListener('click', (event) => {
        const button = event.target.closest('.df-media-modal-trim-btn');
        if (!button) return;

        const modal = button.closest(MODAL_SELECTOR);
        if (!modal) return;

        const scissors = activeScissorsTrigger(modal);
        if (!scissors) return;

        event.preventDefault();
        const bsModal = window.bootstrap?.Modal?.getInstance(modal);
        if (bsModal) {
            modal.addEventListener('hidden.bs.modal', () => { scissors.click(); }, { once: true });
            bsModal.hide();
        } else {
            scissors.click();
        }
    });

    document.addEventListener('click', (event) => {
        const button = event.target.closest('.df-media-modal-rotate-button');
        if (!button) return;

        const modal = button.closest(MODAL_SELECTOR);
        if (modal?.dataset.dfMediaModalRotating === 'true') return;

        const trigger = activeModalMediaTrigger(modal);
        if (!mediaTriggerCanRotate(trigger)) return;

        event.preventDefault();
        currentDfMediaModalTrigger = trigger;
        setDfMediaModalRotating(modal, true);

        const degree = Number(button.dataset.degree);
        if (trigger.dataset.dfMediaType === 'image' && typeof window.rotateImage === 'function') {
            window.rotateImage(trigger, degree);
        } else if (trigger.dataset.dfMediaType === 'video' && typeof window.rotateVideo === 'function') {
            window.rotateVideo(trigger, degree);
        } else {
            setDfMediaModalRotating(modal, false);
        }
    });
};

const initializeDfMediaModal = () => {
    bindDfMediaModalEvents();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeDfMediaModal);
} else {
    initializeDfMediaModal();
}

document.addEventListener('turbolinks:load', initializeDfMediaModal);

window.initializeDfMediaModal = initializeDfMediaModal;
window.refreshDfMediaModalFromTrigger = refreshDfMediaModalFromTrigger;
window.setDfMediaModalRotating = (isRotating) => setDfMediaModalRotating(document.querySelector(MODAL_SELECTOR), isRotating);
